import { Extension, InputRule, Mark, mergeAttributes } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import Suggestion from '@tiptap/suggestion'
import { getWikiLinkSuggestionPopup } from '../components/WikiLinkSuggestionPopup'

// Matches [[Page]], [[Page|Text]], [[Page#anchor]], [[project:Page]], [[project:Page|Text]]
const WIKI_LINK_RE = /(?<!!)\[\[([^\]|#]+)(?:#([^\]|]+))?(?:\|([^\]]+))?\]\]/g
const WIKI_LINK_INPUT_RULE_RE = /(?<!!)\[\[([^\]|#]+)(?:#([^\]|]+))?(?:\|([^\]]+))?\]\]$/

export function buildWikiLinkRaw(attrs: Record<string, unknown>): string {
  let inner = ''
  if (attrs.project)
    inner += `${attrs.project}:`
  inner += attrs.page as string
  if (attrs.anchor)
    inner += `#${attrs.anchor}`
  if (attrs.display)
    inner += `|${attrs.display}`
  return `[[${inner}]]`
}

function parseWikiLinkMatch(pageRaw: string) {
  let project: string | null = null
  let page = pageRaw
  const colonIdx = pageRaw.indexOf(':')
  if (colonIdx !== -1) {
    project = pageRaw.slice(0, colonIdx)
    page = pageRaw.slice(colonIdx + 1)
  }
  return { project, page }
}

export const WikiLink = Mark.create({
  name: 'wikiLink',
  inclusive: false,

  addAttributes() {
    return {
      raw: { default: '' },
      page: { default: '' },
      anchor: { default: null },
      project: { default: null },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-wiki-link]',
        getAttrs: el => ({
          raw: el.getAttribute('data-wiki-link') || '',
          page: el.getAttribute('data-wiki-page') || '',
          anchor: el.getAttribute('data-wiki-anchor') || null,
          project: el.getAttribute('data-wiki-project') || null,
        }),
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes({
        'data-wiki-link': HTMLAttributes.raw,
        'data-wiki-page': HTMLAttributes.page,
        ...(HTMLAttributes.anchor ? { 'data-wiki-anchor': HTMLAttributes.anchor } : {}),
        ...(HTMLAttributes.project ? { 'data-wiki-project': HTMLAttributes.project } : {}),
        'class': 'wiki-link-node',
      }),
      0,
    ]
  },

  renderMarkdown(node, helpers) {
    const text = helpers.renderChildren(node)
    const display = text && text !== node.attrs?.page ? text : null
    return buildWikiLinkRaw({ ...node.attrs, display })
  },

  markdownTokenName: 'wikiLink',

  markdownTokenizer: {
    name: 'wikiLink',
    level: 'inline',
    start: (src: string) => {
      const idx = src.indexOf('[[')
      if (idx > 0 && src[idx - 1] === '!')
        return idx + 1
      return idx
    },
    tokenize: (src: string) => {
      WIKI_LINK_RE.lastIndex = 0
      const m = WIKI_LINK_RE.exec(src)
      // marked.js v16+ passes the full remaining source (not pre-sliced to start
      // position), so only match when the pattern starts at position 0.
      if (!m || m.index !== 0)
        return

      const fullRaw = m[0]
      const pageRaw = m[1] ?? ''
      const anchor = m[2] ?? null
      const display = m[3] ?? null
      const { project, page } = parseWikiLinkMatch(pageRaw)

      return {
        type: 'wikiLink',
        raw: fullRaw,
        page,
        anchor,
        project,
        display,
        tokens: [],
      } as any
    },
  },

  parseMarkdown(token: any, helpers: any) {
    const t = token
    const displayText = t.display || t.page || ''
    return helpers.createTextNode(displayText, [
      {
        type: 'wikiLink',
        attrs: {
          raw: t.raw,
          page: t.page ?? '',
          anchor: t.anchor ?? null,
          project: t.project ?? null,
        },
      },
    ])
  },

  addInputRules() {
    const type = this.type
    return [
      new InputRule({
        find: WIKI_LINK_INPUT_RULE_RE,
        handler: ({ state, range, match }) => {
          const raw = match[0]
          const pageRaw = match[1] ?? ''
          const anchor = match[2] ?? null
          const display = match[3] ?? null
          const { project, page } = parseWikiLinkMatch(pageRaw)
          const { tr } = state
          const text = display || page
          tr.replaceWith(
            range.from,
            range.to,
            state.schema.text(text, [type.create({ raw, page, anchor, project })]),
          )
          tr.scrollIntoView()
        },
      }),
    ]
  },
})

/* ── WikiLink suggestion (triggers on [[) ──────────────────────────── */

export interface WikiPageSuggestionItem {
  id: number
  label: string
  value: string
}

interface WikiLinkSuggestionOptions {
  url: string
}

export const WikiLinkSuggestion = Extension.create<WikiLinkSuggestionOptions>({
  name: 'wikiLinkSuggestion',

  addOptions() {
    return { url: '' }
  },

  addProseMirrorPlugins() {
    const baseUrl = this.options.url
    let abortCtrl: AbortController | undefined

    return [
      Suggestion({
        pluginKey: new PluginKey('wikiLinkSuggestion'),
        editor: this.editor,
        char: '[',
        allowSpaces: true,
        // Only activate after '[[' (two consecutive opening brackets)
        allow: ({ state, range }) => {
          const textBefore = state.doc.textBetween(
            Math.max(0, range.from - 2),
            range.from,
            '\0',
            '\0',
          )
          return textBefore.endsWith('[')
        },

        items: async ({ query }) => {
          abortCtrl?.abort()
          abortCtrl = new AbortController()
          try {
            const res = await fetch(
              baseUrl + encodeURIComponent(query),
              { signal: abortCtrl.signal },
            )
            if (!res.ok)
              return []
            return res.json() as Promise<WikiPageSuggestionItem[]>
          }
          catch {
            return []
          }
          finally {
            abortCtrl = undefined
          }
        },

        command: ({ editor, range, props }) => {
          const item = props as WikiPageSuggestionItem
          const from = range.from - 1 // include the leading '[' before the trigger
          const text = item.label || item.value
          editor.chain().focus().deleteRange({ from, to: range.to }).insertContentAt(from, {
            type: 'text',
            text,
            marks: [{
              type: 'wikiLink',
              attrs: {
                raw: `[[${item.value}]]`,
                page: item.value,
                anchor: null,
                project: null,
              },
            }],
          }).run()
        },

        render: () => ({
          onStart(props) {
            getWikiLinkSuggestionPopup()?.show(props.items, props.clientRect!, props.command)
          },
          onUpdate(props) {
            getWikiLinkSuggestionPopup()?.update(props.items, props.clientRect!)
          },
          onKeyDown({ event }) {
            return getWikiLinkSuggestionPopup()?.handleKeyDown(event) ?? false
          },
          onExit() {
            getWikiLinkSuggestionPopup()?.hide()
          },
        }),
      }),
    ]
  },
})
